import { useEffect } from 'react';
import { useAuth } from '../context/auth.context';
import { UserActionTypes, useUser } from '../context/user.context';
import { checkIdp } from '../helpers/check-idp';
import { DEFAULT_IDP } from '../helpers/constants';
import LoginForm from './login-form.component';
import LoginLoader from './login-loader.component';
import LoginSchema, { useLoginSchema } from './login-schema.component';

export interface LoginResult {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  selectedSchema: string;
}

export interface LoginProps {
  onLoginSuccess?: (result: LoginResult) => void;
  redirectPath?: string;
  loginQueryParams?: unknown;
  username?: string;
}

export default function Login({ onLoginSuccess, redirectPath, loginQueryParams, username }: LoginProps) {
  const { state, dispatch } = useUser();
  const { signinRedirect } = useAuth();
  const { onSchemaSelected } = useLoginSchema(redirectPath, onLoginSuccess);

  // biome-ignore lint/correctness/useExhaustiveDependencies: isAuthenticated and setIdp are intentionally omitted to prevent re-running on auth state changes. This effect should only run when username changes for the initial auto-redirect flow
  useEffect(() => {
    if (username && !isAuthenticated) {
      const autoRedirect = async () => {
        const idp = await checkIdp(username);
        setIdp(idp);
      };

      // Workaround to ensure component is rendered before redirect happens
      setTimeout(autoRedirect);
    }
  }, [username]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: signinRedirect and loginQueryParams are intentionally omitted as they are stable from the Auth context and props, and including them could cause redirect loops
  useEffect(() => {
    if (state.idp && state.loginStatus === 'idp') {
      signinRedirect(state.idp, loginQueryParams);
    }
  }, [state.idp, state.loginStatus]);

  const isAuthenticated = state.auth;
  const tokens = state.tokens;

  const setIdp = (idp: string) => {
    dispatch({
      type: UserActionTypes.UpdateUser,
      payload: {
        auth: false,
        idp,
        isDefaultIdp: idp === DEFAULT_IDP,
        loginStatus: 'idp',
      },
    });
  };

  return (
    <>
      {!isAuthenticated && !username && <LoginForm setIdp={setIdp} />}
      {isAuthenticated && tokens && <LoginSchema idToken={tokens.id_token} onSchemaSelected={onSchemaSelected} />}
      {!isAuthenticated && username && <LoginLoader title="Redirecting to login" />}
    </>
  );
}
